<script setup lang="ts">
/**
 * Filter drawer for the deposit work queue.
 *
 * Slides in from the right. No status field — the queue is locked to
 * PENDING_VERIFICATION; status filtering would defeat the work-queue
 * contract (only-actionable-rows). Pinned "Apply filters" CTA at the
 * bottom per `.claude/rules/ui-kit-theme.md → Drawer chrome for form
 * drawers`.
 *
 * Filter values are owned as a draft inside this component until the
 * operator hits Apply. The header "Reset" link clears every field at
 * once and emits `reset`.
 */
import { reactive, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import Input from '@uiKit/elements/Input.vue'
import DatePicker from '@uiKit/elements/DatePicker.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import FormField from '@uiKit/components/FormField.vue'

export interface DepositRequestsFilterValues {
  fromDate: Date | null
  toDate: Date | null
  investorName: string
  investorEmail: string
}

interface Props {
  visible: boolean
  values: DepositRequestsFilterValues
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'apply', values: DepositRequestsFilterValues): void
  (e: 'reset'): void
}>()

const draft = reactive<DepositRequestsFilterValues>({
  fromDate: props.values.fromDate,
  toDate: props.values.toDate,
  investorName: props.values.investorName,
  investorEmail: props.values.investorEmail,
})

// Re-sync the draft whenever the drawer is reopened so the operator
// always starts from the currently-applied values, not stale draft
// values from a previous open.
watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) return
    draft.fromDate = props.values.fromDate
    draft.toDate = props.values.toDate
    draft.investorName = props.values.investorName
    draft.investorEmail = props.values.investorEmail
  },
)

function handleApply() {
  emit('apply', {
    fromDate: draft.fromDate,
    toDate: draft.toDate,
    investorName: draft.investorName.trim(),
    investorEmail: draft.investorEmail.trim(),
  })
  emit('update:visible', false)
}

function handleReset() {
  draft.fromDate = null
  draft.toDate = null
  draft.investorName = ''
  draft.investorEmail = ''
  emit('reset')
}

function handleClose() {
  emit('update:visible', false)
}
</script>

<template>
  <AppDrawer
    :model-value="visible"
    position="right"
    width="440px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && handleClose()"
  >
    <template #header>
      <DrawerHeader title="Filter requests" subtitle="Refine the queue" @close="handleClose">
        <template #extras>
          <Button label="Reset" variant="text" color="secondary" @click="handleReset" />
        </template>
      </DrawerHeader>
    </template>

    <Box flex direction="col" class="drawer-body">
      <Box flex direction="col" gap="4" class="drawer-body__content">
        <FormField label="From">
          <DatePicker v-model="draft.fromDate" placeholder="DD/MM/YYYY" date-format="dd/mm/yy" />
        </FormField>
        <FormField label="To">
          <DatePicker v-model="draft.toDate" placeholder="DD/MM/YYYY" date-format="dd/mm/yy" />
        </FormField>
        <FormField label="Investor name">
          <Input v-model="draft.investorName" placeholder="Name contains…" />
        </FormField>
        <FormField label="Investor email">
          <Input v-model="draft.investorEmail" placeholder="Email contains…" />
        </FormField>
      </Box>

      <Box class="drawer-body__actions">
        <Button label="Apply filters" color="primary" block size="large" @click="handleApply" />
      </Box>
    </Box>
  </AppDrawer>
</template>

<style scoped>
.drawer-body {
  min-height: 100%;
  padding: 1.25rem 1.5rem;
}

.drawer-body__content {
  flex: 1;
}

.drawer-body__actions {
  margin-top: auto;
  padding-top: 1rem;
}

@media (max-width: 992px) {
  .drawer-body {
    padding: 1rem;
  }
}
</style>
